import PlayCircle from "mdi-material-ui/PlayCircle";
import TextLong from "mdi-material-ui/TextLong";
import Attachment from "mdi-material-ui/Attachment";
import VolumeHigh from "mdi-material-ui/VolumeHigh";
import ClockOutline from "mdi-material-ui/ClockOutline";
import ClipboardTextOutline from "mdi-material-ui/ClipboardTextOutline";
import moment from "moment";

const getVideosInformation = (videos_info, video_id, field) => {
  try {
    if (!videos_info) return null;

    const item = videos_info.find((item) => item.video_id === video_id);

    return item[field];
  } catch (e) {
    //console.log("getVideosInformation", e);
    return null;
  }
};

export const AttachmentsInfo = ({ lesson, onClick }) => {
  if (lesson.files_content === undefined || lesson.files_content === null) return undefined;
  if (lesson.files_content.length === 0) return undefined;

  return (
    <span className="lesson-materials-chip" onClick={onClick}>
      <Attachment />
      Materiais
    </span>
  );
};

/******************************************************************
 * ##: LessonInfo
 * Small rounded tile with the lesson-type icon (video / audio /
 * text / questionnaire). Video covers every source (library/Bunny,
 * external YouTube/Vimeo/Wistia, ...) since they share the "video"
 * type. Used in the playlist rows and the lesson search list.
 * @param {Object} lesson - lesson record (uses lesson_type)
 * @returns {JSX.Element} - the `.lesson-type-icon` tile
 * History:
 * 05-07-2026: Rebuilt as a style-v3 icon tile
 ******************************************************************/
export const LessonInfo = ({ lesson }) => {
  const type = lesson?.lesson_type;
  let TypeIcon = PlayCircle;
  if (type === "text") TypeIcon = TextLong;
  else if (type === "questionnaire") TypeIcon = ClipboardTextOutline;
  else if (type === "audio") TypeIcon = VolumeHigh;

  return (
    <span className={`lesson-type-icon lesson-type-icon--${type || "video"}`}>
      <TypeIcon />
    </span>
  );
};

const LibraryVideoTime = ({ videos_info, video_source }) => {
  if (!videos_info) return <></>;
  if (!video_source) return <></>;

  // Library (Bunny) duration lives in courses_videos.duration (seconds); the old
  // "maxDurationSeconds" was a Cloudflare Stream leftover and is never populated.
  const videoSeconds = getVideosInformation(videos_info, video_source, "duration") * 1000;
  if (!videoSeconds) return <></>;

  let timeFormat = "mm[m] ss[s]";
  if (videoSeconds > 3600000) {
    timeFormat = "hh[h] mm[m] ss[s]";
  }

  return (
    <>
      <ClockOutline sx={{ fontSize: "0.7rem", marginRight: 1 }} />
      {moment.utc(videoSeconds).format(timeFormat)}
    </>
  );
};

const VideoTime = ({ video_length }) => {
  if (!video_length) return <></>;

  const videoSeconds = video_length * 1000;
  //  console.log("VideoTime", videoSeconds);
  let timeFormat = "mm[m] ss[s]";
  if (videoSeconds > 3600000) {
    timeFormat = "hh[h] mm[m] ss[s]";
  }

  return <>{moment.utc(videoSeconds).format(timeFormat)}</>;
};

export const LessonLength = ({ videos_info, lesson }) => {
  if (lesson?.lesson_type === "video" && lesson?.video_origin === "library") {
    return (
      <div className="lesson-length" style={{ marginTop: 4 }}>
        <LibraryVideoTime videos_info={videos_info} video_source={lesson?.video_source} />
      </div>
    );
  }

  if (lesson?.lesson_type === "video" && lesson?.video_origin === "external" && lesson?.video_length !== undefined) {
    return (
      <div className="lesson-length" style={{ marginTop: 4 }}>
        <ClockOutline sx={{ fontSize: "0.7rem", marginRight: 1 }} />
        <VideoTime video_length={lesson?.video_length} />
      </div>
    );
  }

  if (lesson?.lesson_type === "audio" && lesson?.audio_source?.duration !== undefined) {
    return (
      <div className="lesson-length" style={{ marginTop: 4 }}>
        <ClockOutline sx={{ fontSize: "0.7rem", marginRight: 1 }} />
        {moment.utc(lesson?.audio_source?.duration * 1000).format("mm[m] ss[s]")}
      </div>
    );
  }
};

export const isChapterAvailable = (chapter, courseAddedAt, hasTeaserAccess = false) => {
  if (!chapter) return false;
  const chapterStatusCode = Number(chapter.status_code);
  if (chapterStatusCode === 5 || chapterStatusCode === 6) return true;
  if (hasTeaserAccess === true && chapter.teaser_locked === true) return false;
  if (chapterStatusCode === 0) return false;
  if (chapterStatusCode === 1) return true;

  // Dripping
  if (chapterStatusCode === 2 && chapter.drip_days !== undefined) {
    const avaliableDate = moment.utc(courseAddedAt).add(chapter.drip_days, "days");

    if (moment.utc().isAfter(avaliableDate)) return true;
  }

  // Avaliable On
  if (chapterStatusCode === 3 && chapter.avaliable_on !== undefined) {
    if (moment.utc().isAfter(chapter.avaliable_on)) return true;
  }

  return false;
};

// export const isChapterAvailable__old = (dripDays, courseAddedAt, status_code) => {
//   if (status_code === 1) return true;
//   const avaliableDate = moment.utc(courseAddedAt).add(dripDays, "days");
//   if (moment.utc().isAfter(avaliableDate)) return true;
//   return false;
// };

export const ChapterAvaliableIn = ({ chapter, courseAddedAt, courseEndDate, hasTeaserAccess = false }) => {
  if (!chapter) return <></>;
  const chapterStatusCode = Number(chapter?.status_code);
  if (!chapterStatusCode) return <></>;
  if (chapterStatusCode === 5 || chapterStatusCode === 6) return <></>;
  if (hasTeaserAccess === true && chapter.teaser_locked === true) {
    return (
      <div className="soon-chip-container">
        <span className="soon-chip soon-chip--warning">Não disponível na versão gratuita</span>
      </div>
    );
  }

  let fromNow = undefined;
  let avaliable_on = undefined;
  let endDate = undefined;

  // Dripping
  if (chapterStatusCode === 2 && chapter.drip_days !== undefined) {
    const avaliableDate = moment.utc(courseAddedAt).add(chapter.drip_days, "days");
    if (moment.utc().isAfter(avaliableDate)) return <></>;

    fromNow = moment.utc(avaliableDate).fromNow(true);
    avaliable_on = moment.utc(avaliableDate);
  }

  // Avaliable On
  if (chapterStatusCode === 3 && chapter.avaliable_on !== undefined) {
    if (moment.utc().isAfter(chapter.avaliable_on)) return <></>;

    fromNow = moment.utc(chapter.avaliable_on).fromNow(true);
    avaliable_on = moment.utc(chapter.avaliable_on);
  }

  if (courseEndDate !== undefined && courseEndDate !== null) {
    // Check if and date is before avaliable_on
    endDate = moment.utc(courseEndDate);
    if (endDate.isBefore(avaliable_on)) {
      return (
        <div className="soon-chip-container">
          <span className="soon-chip soon-chip--warning">{`Disponível até ${moment.utc(courseEndDate).local().format("DD-MM-YYYY")}`}</span>
        </div>
      );
    }
  }

  if (chapterStatusCode === 0 || (chapterStatusCode === 1 && courseEndDate !== undefined && courseEndDate !== null)) {
    return (
      <div style={{ margin: 0, padding: 0, lineHeight: "10px" }}>
        <small style={{ fontSize: "10px" }}>Disponível até {moment.utc(courseEndDate).local().format("DD-MM-YYYY")}</small>
      </div>
    );
  }
  if (fromNow !== undefined) {
    return (
      <div className="soon-chip-container">
        <span className="soon-chip soon-chip--info">
          <ClockOutline />
          {`Disponível dentro de ${fromNow}`}
        </span>
      </div>
    );
  }
  return <></>;
};
